import React from 'react';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { signIn, getEntitlements } from 'State/User/UserActions';
import { setUser } from 'Utils/UserCookie';
import { hideOverlay, showOverlay } from 'State/Layouts/LayoutActions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { fetchSubscriptionStatus } from 'State/Subscriptions/SubscriptionsActions';
import { isSheerProductSKU, getProductSKUSheerType } from 'Utils/SheerIdHelpers';
import { getFeatureFlag } from 'Utils/FeatureFlags';

export const withAuth = (WrappedComponent) => (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isPlanSelected, selectedPlan } = useSelector((s) => s.plans);
  const isBango = useSelector((s) => s.integrations.bango.subscriptionId);
  const bangoProductId = useSelector((s) => s.integrations.bango.productId);
  const routes = useSelector((s) => s.appConfig.routes);
  const location = useLocation();
  const { isGifting } = useSelector((s) => s.purchaseMode);
  const { pathname = '' } = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const isDeviceActivation = pathname.includes('tv');
  const isSheerIdVerification =
    getFeatureFlag('featureFlag_sheerID') && location?.state?.from?.pathname.includes(routes.sheerIdVerification);

  const handleSignInSuccess = async (userData, disableOverlay, isInSignUp) => {
    if (!disableOverlay && !isDeviceActivation) {
      dispatch(showOverlay('Signing in to your account...'));
    }

    await setUser(userData);
    const { hasEntitlement } = await dispatch(getEntitlements(1));
    await dispatch(fetchSubscriptionStatus());
    /**
     * please make sure we call getEntitlements before signIn since we need to check the entitlement
     * in renderRedirect SignInContainer.js; if we call signIn before getEntitlements,
     * it triggers component re-render and entitlementCookie is not set
     */
    dispatch(signIn(userData));
    if (!isInSignUp) trackEvent(TRACKING_ENUMS.EVENT_LOGIN_COMPLETE);

    if (!disableOverlay && !isDeviceActivation) {
      dispatch(hideOverlay());
    }
    return hasEntitlement;
  };

  const handleSignUpSuccess = () => {
    let path = routes.payment;
    if (isBango) {
      path = routes.bangoFinal;
      if (isSheerProductSKU(bangoProductId)) {
        const sheerType = getProductSKUSheerType(bangoProductId);
        path = sheerType ? `${routes.sheerIdVerification}?sheerVerification=${sheerType}` : routes.sheerIdVerification;
      }
    } else if (isSheerIdVerification) {
      const sheerType = urlParams.get('sheerVerification');
      path = sheerType ? `${routes.sheerIdVerification}?sheerVerification=${sheerType}` : routes.sheerIdVerification;
    } else if (isGifting) {
      path = routes.giftingReviewAndRedeem;
    } else if (isPlanSelected && selectedPlan && selectedPlan.hasMerchandise) {
      path = routes.shipping;
    } else if (!isPlanSelected) {
      path = routes.signupPlans;
    }
    navigate(path);
  };

  return <WrappedComponent {...props} onSignInSuccess={handleSignInSuccess} onSignUpSuccess={handleSignUpSuccess} />;
};
